import React from 'react';
import { Link } from 'react-router-dom';
import { 
  FiBriefcase, FiArrowRight, FiGrid, FiUsers, FiLayers, FiDollarSign,
  FiBarChart2, FiShield, FiCreditCard, FiStar, FiCheckCircle, FiSettings
} from 'react-icons/fi';
import PublicPageHeader from '../../../components/common/PublicPageHeader';
import PublicPageFooter from '../../../components/common/PublicPageFooter';
import DocsSidebar from '../../../components/docs/DocsSidebar';

const OrganizationWorkspaceDocs = () => {
  const features = [
    {
      icon: FiLayers,
      title: 'Departments',
      description: 'Organize your organization into departments with their own projects, budgets, and teams.',
      link: '/docs/organization/departments',
      color: 'bg-purple-50 dark:bg-purple-900/30 text-purple-600'
    },
    {
      icon: FiUsers,
      title: 'Teams',
      description: 'Create teams within departments for focused collaboration on specific initiatives.',
      link: '/docs/organization/teams',
      color: 'bg-blue-50 dark:bg-blue-900/30 text-blue-600'
    },
    {
      icon: FiDollarSign,
      title: 'Finance & Budgets',
      description: 'Comprehensive budget management with requests, approvals, and department allocations.',
      link: '/docs/organization/finance',
      color: 'bg-green-50 dark:bg-green-900/30 text-green-600'
    },
    {
      icon: FiBarChart2,
      title: 'Analytics',
      description: 'Organization-wide analytics and reporting for data-driven decisions.',
      link: '/docs/organization/analytics',
      color: 'bg-orange-50 dark:bg-orange-900/30 text-orange-600'
    },
    {
      icon: FiShield,
      title: 'Security & Compliance',
      description: 'Enterprise-grade security settings, audit logs, and compliance features.',
      link: '/docs/admin/security',
      color: 'bg-red-50 dark:bg-red-900/30 text-red-600'
    },
    {
      icon: FiCreditCard,
      title: 'Billing & Subscription',
      description: 'Manage organization billing, subscriptions, and payment methods.',
      link: '/docs/organization/billing',
      color: 'bg-cyan-50 dark:bg-cyan-900/30 text-cyan-600'
    }
  ];

  const hierarchy = [
    {
      level: 'Organization',
      description: 'Top-level entity containing all departments, teams, and users',
      icon: FiBriefcase
    },
    {
      level: 'Departments',
      description: 'Major divisions within the organization (Engineering, Marketing, etc.)',
      icon: FiLayers
    },
    {
      level: 'Teams',
      description: 'Focused groups within departments working on specific areas',
      icon: FiUsers
    },
    {
      level: 'Projects',
      description: 'Work containers with tasks, owned by teams or departments',
      icon: FiGrid
    }
  ];

  return (
    <div className="min-h-screen bg-white dark:bg-gray-900">
      <PublicPageHeader />
      
      <div className="flex">
        <DocsSidebar currentPath="/docs/organization" />
        
        <main className="flex-1 max-w-4xl mx-auto px-6 py-12">
          {/* Breadcrumb */}
          <nav className="flex items-center space-x-2 text-sm text-gray-500 dark:text-gray-400 mb-8">
            <Link to="/docs" className="hover:text-blue-600">Docs</Link>
            <span>/</span>
            <span className="text-gray-900 dark:text-white">Organization Workspace</span>
          </nav>

          {/* Header */}
          <div className="mb-12">
            <div className="inline-flex p-2 bg-purple-100 dark:bg-purple-900/30 rounded-xl mb-4">
              <FiBriefcase className="w-6 h-6 text-purple-600" />
            </div>
            <h1 className="text-4xl font-bold text-gray-900 dark:text-white mb-4">Organization Workspace</h1>
            <p className="text-xl text-gray-600 dark:text-gray-300">
              Enterprise-grade workspace for companies with departments, teams, role-based access, 
              comprehensive finance management, and advanced analytics.
            </p>
          </div>

          {/* Overview */}
          <section className="mb-16">
            <div className="bg-gradient-to-r from-purple-50 to-indigo-50 dark:from-purple-900/20 dark:to-indigo-900/20 rounded-2xl p-8">
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">What is Organization Workspace?</h2>
              <p className="text-gray-600 dark:text-gray-300 mb-6">
                The Organization Workspace is designed for companies and enterprises that need 
                structured team management, department hierarchies, budget controls, and 
                comprehensive role-based access control.
              </p>
              <div className="grid md:grid-cols-2 gap-4">
                <div className="bg-white dark:bg-gray-800 rounded-xl p-4">
                  <div className="flex items-center space-x-2 mb-2">
                    <FiStar className="text-yellow-500" />
                    <span className="font-medium text-gray-900 dark:text-white">Best For</span>
                  </div>
                  <ul className="text-sm text-gray-600 dark:text-gray-300 space-y-1">
                    <li>• Medium to large companies</li>
                    <li>• Organizations with departments</li>
                    <li>• Teams needing budget management</li>
                    <li>• Enterprises requiring compliance</li>
                  </ul>
                </div>
                <div className="bg-white dark:bg-gray-800 rounded-xl p-4">
                  <div className="flex items-center space-x-2 mb-2">
                    <FiStar className="text-yellow-500" />
                    <span className="font-medium text-gray-900 dark:text-white">Key Benefits</span>
                  </div>
                  <ul className="text-sm text-gray-600 dark:text-gray-300 space-y-1">
                    <li>• Structured hierarchy (Org → Dept → Team)</li>
                    <li>• Comprehensive RBAC system</li>
                    <li>• Budget management & approvals</li>
                    <li>• Organization-wide analytics</li>
                  </ul>
                </div>
              </div>
            </div>
          </section>

          {/* Organization Setup */}
          <section className="mb-16" id="setup">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">Setting Up Your Organization</h2>
            <p className="text-gray-600 dark:text-gray-300 mb-6">
              Follow these steps to set up your organization workspace:
            </p>

            <div className="bg-gray-50 dark:bg-gray-800 rounded-2xl p-6">
              <ol className="space-y-4">
                <li className="flex items-start space-x-3">
                  <span className="flex-shrink-0 w-6 h-6 bg-purple-600 text-white rounded-full flex items-center justify-center text-sm">1</span>
                  <div>
                    <span className="font-medium text-gray-900 dark:text-white">Register as Organization</span>
                    <p className="text-sm text-gray-600 dark:text-gray-300">Choose "Organization" during registration or upgrade from personal workspace</p>
                  </div>
                </li>
                <li className="flex items-start space-x-3">
                  <span className="flex-shrink-0 w-6 h-6 bg-purple-600 text-white rounded-full flex items-center justify-center text-sm">2</span>
                  <div>
                    <span className="font-medium text-gray-900 dark:text-white">Configure Organization Settings</span>
                    <p className="text-sm text-gray-600 dark:text-gray-300">Set organization name, logo, branding, and default settings</p>
                  </div>
                </li>
                <li className="flex items-start space-x-3">
                  <span className="flex-shrink-0 w-6 h-6 bg-purple-600 text-white rounded-full flex items-center justify-center text-sm">3</span>
                  <div>
                    <span className="font-medium text-gray-900 dark:text-white">Create Departments</span>
                    <p className="text-sm text-gray-600 dark:text-gray-300">Structure your organization with departments like Engineering, Marketing, etc.</p>
                  </div>
                </li>
                <li className="flex items-start space-x-3">
                  <span className="flex-shrink-0 w-6 h-6 bg-purple-600 text-white rounded-full flex items-center justify-center text-sm">4</span>
                  <div>
                    <span className="font-medium text-gray-900 dark:text-white">Create Teams</span>
                    <p className="text-sm text-gray-600 dark:text-gray-300">Form teams within departments for focused work groups</p>
                  </div>
                </li>
                <li className="flex items-start space-x-3">
                  <span className="flex-shrink-0 w-6 h-6 bg-purple-600 text-white rounded-full flex items-center justify-center text-sm">5</span>
                  <div>
                    <span className="font-medium text-gray-900 dark:text-white">Invite Members</span>
                    <p className="text-sm text-gray-600 dark:text-gray-300">Add users and assign them to departments and teams with appropriate roles</p>
                  </div>
                </li>
              </ol>
            </div>
          </section>

          {/* Organization Hierarchy */}
          <section className="mb-16">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">Organization Hierarchy</h2>
            <p className="text-gray-600 dark:text-gray-300 mb-6">
              Understanding the hierarchy helps you structure your organization effectively:
            </p>

            <div className="space-y-4">
              {hierarchy.map((item, index) => {
                const Icon = item.icon;
                const indent = index * 24;
                return (
                  <div 
                    key={index} 
                    className="flex items-center space-x-4 p-4 border border-gray-200 dark:border-gray-700 rounded-xl"
                    style={{ marginLeft: `${indent}px` }}
                  >
                    <div className="w-10 h-10 bg-purple-100 dark:bg-purple-900/30 rounded-lg flex items-center justify-center">
                      <Icon className="w-5 h-5 text-purple-600" />
                    </div>
                    <div>
                      <h3 className="font-medium text-gray-900 dark:text-white">{item.level}</h3>
                      <p className="text-sm text-gray-600 dark:text-gray-300">{item.description}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </section>

          {/* Features Grid */}
          <section className="mb-16">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-8">Features</h2>
            <div className="grid md:grid-cols-2 gap-6">
              {features.map((feature, index) => {
                const Icon = feature.icon;
                return (
                  <Link
                    key={index}
                    to={feature.link}
                    className="group bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700 p-6 shadow-sm hover:shadow-md hover:border-purple-200 transition-all"
                  >
                    <div className={`w-12 h-12 rounded-xl ${feature.color} flex items-center justify-center mb-4`}>
                      <Icon className="w-6 h-6" />
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2 group-hover:text-purple-600 transition-colors">
                      {feature.title}
                    </h3>
                    <p className="text-gray-600 dark:text-gray-300 text-sm mb-4">{feature.description}</p>
                    <div className="flex items-center text-purple-600 text-sm font-medium">
                      <span>Learn more</span>
                      <FiArrowRight className="w-4 h-4 ml-1 group-hover:translate-x-1 transition-transform" />
                    </div>
                  </Link>
                );
              })}
            </div>
          </section>

          {/* Role Overview */}
          <section className="mb-16">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">Organization Roles</h2>
            <p className="text-gray-600 dark:text-gray-300 mb-6">
              Organization workspaces have a comprehensive role-based access control system:
            </p>

            <div className="space-y-3">
              <div className="flex items-center justify-between p-4 bg-red-50 rounded-xl border border-red-100">
                <div className="flex items-center space-x-3">
                  <FiShield className="w-5 h-5 text-red-600" />
                  <div>
                    <span className="font-medium text-gray-900 dark:text-white">Organization Admin</span>
                    <p className="text-sm text-gray-600 dark:text-gray-300">Full organization management access</p>
                  </div>
                </div>
                <Link to="/docs/admin/roles" className="text-sm text-red-600 hover:underline">
                  Learn more →
                </Link>
              </div>

              <div className="flex items-center justify-between p-4 bg-orange-50 rounded-xl border border-orange-100">
                <div className="flex items-center space-x-3">
                  <FiLayers className="w-5 h-5 text-orange-600" />
                  <div>
                    <span className="font-medium text-gray-900 dark:text-white">Department Manager</span>
                    <p className="text-sm text-gray-600 dark:text-gray-300">Manages specific department</p>
                  </div>
                </div>
                <Link to="/docs/admin/roles" className="text-sm text-orange-600 hover:underline">
                  Learn more →
                </Link>
              </div>

              <div className="flex items-center justify-between p-4 bg-blue-50 dark:bg-blue-900/30 rounded-xl border border-blue-100">
                <div className="flex items-center space-x-3">
                  <FiUsers className="w-5 h-5 text-blue-600" />
                  <div>
                    <span className="font-medium text-gray-900 dark:text-white">Team Lead</span>
                    <p className="text-sm text-gray-600 dark:text-gray-300">Leads team within department</p>
                  </div>
                </div>
                <Link to="/docs/admin/roles" className="text-sm text-blue-600 hover:underline">
                  Learn more →
                </Link>
              </div>

              <div className="flex items-center justify-between p-4 bg-green-50 rounded-xl border border-green-100">
                <div className="flex items-center space-x-3">
                  <FiCheckCircle className="w-5 h-5 text-green-600" />
                  <div>
                    <span className="font-medium text-gray-900 dark:text-white">Employee</span>
                    <p className="text-sm text-gray-600 dark:text-gray-300">Standard organization member</p>
                  </div>
                </div>
                <Link to="/docs/admin/roles" className="text-sm text-green-600 hover:underline">
                  Learn more →
                </Link>
              </div>
            </div>
          </section>

          {/* Navigation */}
          <section className="mb-16">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">Explore More</h2>
            <div className="grid md:grid-cols-2 gap-4">
              <Link
                to="/docs/organization/departments"
                className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-800 rounded-xl hover:bg-purple-50 dark:hover:bg-purple-900/20 border border-transparent hover:border-purple-200 transition-all"
              >
                <span className="font-medium text-gray-900 dark:text-white">Department Management</span>
                <FiArrowRight className="text-gray-400 dark:text-gray-500" />
              </Link>
              <Link
                to="/docs/organization/teams"
                className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-800 rounded-xl hover:bg-purple-50 dark:hover:bg-purple-900/20 border border-transparent hover:border-purple-200 transition-all"
              >
                <span className="font-medium text-gray-900 dark:text-white">Team Management</span>
                <FiArrowRight className="text-gray-400 dark:text-gray-500" />
              </Link>
              <Link
                to="/docs/organization/finance"
                className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-800 rounded-xl hover:bg-purple-50 dark:hover:bg-purple-900/20 border border-transparent hover:border-purple-200 transition-all"
              >
                <span className="font-medium text-gray-900 dark:text-white">Finance & Budgets</span>
                <FiArrowRight className="text-gray-400 dark:text-gray-500" />
              </Link>
              <Link
                to="/docs/admin"
                className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-800 rounded-xl hover:bg-red-50 border border-transparent hover:border-red-200 transition-all"
              >
                <span className="font-medium text-gray-900 dark:text-white">Admin & Permissions</span>
                <FiArrowRight className="text-gray-400 dark:text-gray-500" />
              </Link>
            </div>
          </section>

          {/* Article Footer */}
          <div className="border-t border-gray-200 dark:border-gray-700 pt-8">
            <div className="flex items-center justify-between">
              <div className="text-sm text-gray-500 dark:text-gray-400">
                Last updated: January 27, 2026
              </div>
              <div className="flex items-center space-x-4">
                <span className="text-sm text-gray-500 dark:text-gray-400">Was this helpful?</span>
                <button className="px-3 py-1 text-sm bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 transition-colors">
                  Yes
                </button>
                <button className="px-3 py-1 text-sm bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 transition-colors">
                  No
                </button>
              </div>
            </div>
          </div>
        </main>
      </div>

      <PublicPageFooter />
    </div>
  );
};

export default OrganizationWorkspaceDocs;
